/* eslint-disable no-console */
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getOperacionesFactoring, liquidarOperacionFactoring } from "../../utils/factoring";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import styles from "./Factoring.module.scss";

const ESTADO_OPCIONES = [
  { value: "todos", label: "Todos" },
  { value: "activa", label: "Activas" },
  { value: "liquidada", label: "Liquidadas" },
];

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [y, m, d] = f.split("-");
  return `${d}/${m}/${y}`;
};

const Factoring = () => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const monedaPrincipal = pais === "Chile" ? "CLP" : "PEN";

  const [operaciones, setOperaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [liquidandoId, setLiquidandoId] = useState(null);

  const cargar = async () => {
    if (!pais) return;
    setLoading(true);
    try {
      const data = await getOperacionesFactoring(pais);
      setOperaciones(data);
    } catch (err) {
      console.error("Error cargando factoring:", err);
      toast.error("Error cargando operaciones de factoring");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  const operacionesFiltradas = useMemo(
    () => (filtroEstado === "todos" ? operaciones : operaciones.filter((o) => o.estado === filtroEstado)),
    [operaciones, filtroEstado]
  );

  const resumen = useMemo(() => {
    const totalCedido = operaciones.reduce((s, o) => s + Number(o.monto_factura || 0), 0);
    const totalCosto = operaciones.reduce(
      (s, o) => s + Number(o.costo_financiero || 0) + Number(o.iva_financiamiento || 0),
      0
    );
    const activas = operaciones.filter((o) => o.estado === "activa").length;
    const liquidadas = operaciones.filter((o) => o.estado === "liquidada").length;
    return { totalCedido, totalCosto, activas, liquidadas };
  }, [operaciones]);

  const handleLiquidar = async (op) => {
    const fechaDefault = op.fecha_cobro_final || new Date().toISOString().split("T")[0];
    const fechaPagoReal = window.prompt(
      `Fecha de cobro del remanente (YYYY-MM-DD) — Folio ${op.factura?.folio || op.factura_sii_emitida_id}:`,
      fechaDefault
    );
    if (fechaPagoReal === null) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaPagoReal)) {
      toast.error("Fecha inválida (formato YYYY-MM-DD)");
      return;
    }
    setLiquidandoId(op.id);
    try {
      await liquidarOperacionFactoring(op, { pais, fechaPagoReal, monedaPrincipal });
      toast.success("Operación liquidada y cobro del remanente registrado en caja");
      setOperaciones((prev) =>
        prev.map((o) =>
          o.id === op.id
            ? {
                ...o,
                estado: "liquidada",
                fecha_pago_real: fechaPagoReal,
                updated_at: new Date().toISOString(),
              }
            : o
        )
      );
    } catch (err) {
      console.error("Error liquidando:", err);
      toast.error("Error al liquidar la operación");
    } finally {
      setLiquidandoId(null);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Header title="Factoring" />
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            <div className={styles.resumen}>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Total cedido</div>
                <div className={`${styles.cardValue} ${styles.cardCedido}`}>
                  {formatAmount(resumen.totalCedido, monedaPrincipal)}
                </div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Costo acumulado (con IVA)</div>
                <div className={`${styles.cardValue} ${styles.cardCosto}`}>
                  {formatAmount(resumen.totalCosto, monedaPrincipal)}
                </div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Operaciones activas</div>
                <div className={`${styles.cardValue} ${styles.cardActivas}`}>{resumen.activas}</div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Liquidadas</div>
                <div className={`${styles.cardValue} ${styles.cardLiquidadas}`}>{resumen.liquidadas}</div>
              </div>
            </div>

            <div className={styles.filtros}>
              <label htmlFor="filtroEstado" style={{ color: "#666", margin: 0 }}>
                Estado:
              </label>
              <select
                id="filtroEstado"
                className={`form-select form-select-sm ${styles.filtroSelect}`}
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
              >
                {ESTADO_OPCIONES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span style={{ color: "#999", fontSize: "0.78rem", marginLeft: "auto" }}>
                {operacionesFiltradas.length} de {operaciones.length}
              </span>
            </div>

            {operacionesFiltradas.length === 0 ? (
              <div className={styles.empty}>Sin operaciones de factoring registradas.</div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fecha op.</th>
                      <th>Factura</th>
                      <th>Cliente</th>
                      <th>Empresa factoring</th>
                      <th className={styles.numCol}>Monto factura</th>
                      <th className={styles.numCol}>Capital adelantado</th>
                      <th className={styles.numCol}>Costo financiero</th>
                      <th className={styles.numCol}>IVA financ.</th>
                      <th className={styles.numCol}>Remanente</th>
                      <th>Cobro final est.</th>
                      <th>Pago real</th>
                      <th>Estado</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operacionesFiltradas.map((op) => {
                      const esActiva = op.estado === "activa";
                      return (
                        <tr key={op.id}>
                          <td>{formatFecha(op.fecha_operacion)}</td>
                          <td>{op.factura?.folio || "—"}</td>
                          <td
                            style={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={op.factura?.razon_social_receptor || ""}
                          >
                            {op.factura?.razon_social_receptor || "—"}
                          </td>
                          <td>{op.proveedor?.nombre || "—"}</td>
                          <td className={styles.numCol}>{formatAmount(op.monto_factura, monedaPrincipal)}</td>
                          <td className={styles.numCol}>{formatAmount(op.capital_adelantado, monedaPrincipal)}</td>
                          <td className={styles.numCol} style={{ color: "#c62828" }}>
                            {formatAmount(op.costo_financiero, monedaPrincipal)}
                          </td>
                          <td className={styles.numCol} style={{ color: "#c62828" }}>
                            {formatAmount(op.iva_financiamiento, monedaPrincipal)}
                          </td>
                          <td className={styles.numCol} style={{ color: "#2e7d32", fontWeight: 600 }}>
                            {formatAmount(
                              op.monto_remanente ?? Number(op.monto_factura) - Number(op.capital_adelantado),
                              monedaPrincipal
                            )}
                          </td>
                          <td>{formatFecha(op.fecha_cobro_final)}</td>
                          <td
                            style={{
                              fontWeight: op.fecha_pago_real ? 600 : 400,
                              color: op.fecha_pago_real ? "#2e7d32" : "#999",
                            }}
                          >
                            {formatFecha(op.fecha_pago_real)}
                          </td>
                          <td>
                            <span
                              className={`${styles.estadoBadge} ${
                                esActiva ? styles.estadoActiva : styles.estadoLiquidada
                              }`}
                            >
                              {esActiva ? "Activa" : "Liquidada"}
                            </span>
                          </td>
                          <td>
                            {esActiva && (
                              <button
                                type="button"
                                className={styles.btnLiquidar}
                                onClick={() => handleLiquidar(op)}
                                disabled={liquidandoId === op.id}
                              >
                                {liquidandoId === op.id ? "..." : "Liquidar"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Factoring;
